// Mercapoli - Checkout Page con Cupones, DIGGII y Stripe
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, CreditCard, Banknote, Loader2, CheckCircle, Tag, X, Coins, Minus, Plus, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useCity } from '../../context/CityContext';
import { pedidos as pedidosApi, cupones as cuponesApi, diggii as diggiiApi, payments } from '../../lib/api';
import { toast } from 'sonner';

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { ciudad, formatPrice, getMonedaSimbolo, getMonedaCodigo } = useCity();
  const { 
    items, 
    restauranteId,
    restauranteInfo,
    subtotal, 
    costoEnvio, 
    isEmpty,
    clearCart
  } = useCart();

  const [loading, setLoading] = useState(false);
  const [cuponLoading, setCuponLoading] = useState(false);
  const [codigoCupon, setCodigoCupon] = useState('');
  const [cuponAplicado, setCuponAplicado] = useState(null);
  const [descuento, setDescuento] = useState(0);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  
  // DIGGII state
  const [diggiiData, setDiggiiData] = useState(null);
  const [usarDiggii, setUsarDiggii] = useState(false);
  const [diggiiUsar, setDiggiiUsar] = useState(0);
  const [descuentoDiggii, setDescuentoDiggii] = useState(0);
  
  // Currency info
  const monedaSimbolo = getMonedaSimbolo();
  const monedaCodigo = getMonedaCodigo();
  const tasaCambio = ciudad?.tasa_usd || 7.8;
  
  const [formData, setFormData] = useState({
    direccion_entrega: user?.direccion || '',
    notas: '',
    metodo_pago: 'efectivo'
  });

  const loadDiggiiBalance = async () => {
    try {
      const data = await diggiiApi.getBalance();
      setDiggiiData(data);
    } catch (error) {
      console.error('Error cargando DIGGII:', error);
    }
  };

  useEffect(() => {
    loadDiggiiBalance();
    
    // Check if payment was cancelled
    if (searchParams.get('cancelled') === 'true') {
      setPaymentCancelled(true);
      toast.error('El pago fue cancelado. Puedes intentar nuevamente.');
    }
  }, [searchParams]);

  // Redirigir si el carrito está vacío
  if (isEmpty) {
    navigate('/app/carrito');
    return null;
  }

  const totalSinDiggii = subtotal - descuento + costoEnvio;
  const total = totalSinDiggii - descuentoDiggii;

  // Calcular máximo DIGGII a usar
  const maxDiggiiPorPedido = diggiiData ? Math.min(
    diggiiData.balance,
    totalSinDiggii / (diggiiData.valor_en_local / diggiiData.balance || 1)
  ) : 0;

  const handleDiggiiChange = (usar) => {
    setUsarDiggii(usar);
    if (!usar) {
      setDiggiiUsar(0);
      setDescuentoDiggii(0);
    } else if (diggiiData && diggiiData.balance > 0) {
      // Por defecto usar todo el balance disponible
      const maxUsar = Math.min(diggiiData.balance, maxDiggiiPorPedido);
      setDiggiiUsar(maxUsar);
      const descLocal = maxUsar * (diggiiData.valor_en_local / diggiiData.balance);
      setDescuentoDiggii(Math.min(descLocal, totalSinDiggii));
    }
  };

  const handleDiggiiSliderChange = (value) => {
    const cantidad = value[0];
    setDiggiiUsar(cantidad);
    if (diggiiData && diggiiData.balance > 0) {
      const descLocal = cantidad * (diggiiData.valor_en_local / diggiiData.balance);
      setDescuentoDiggii(Math.min(descLocal, totalSinDiggii));
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const aplicarCupon = async () => {
    if (!codigoCupon.trim()) {
      toast.error('Ingresa un código de cupón');
      return;
    }

    setCuponLoading(true);
    try {
      const resultado = await cuponesApi.validar({
        codigo: codigoCupon,
        subtotal: subtotal,
        restaurante_id: restauranteId
      });

      setCuponAplicado(resultado);
      setDescuento(resultado.descuento);
      toast.success(resultado.mensaje);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cupón no válido');
      setCuponAplicado(null);
      setDescuento(0);
    }
    setCuponLoading(false);
  };

  const removerCupon = () => {
    setCuponAplicado(null);
    setDescuento(0);
    setCodigoCupon('');
    toast.info('Cupón removido');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar ciudad seleccionada (P0: OBLIGATORIO)
    if (!ciudad?.ciudad_id) {
      toast.error('Por favor selecciona tu ciudad antes de realizar el pedido');
      return;
    }
    
    if (!formData.direccion_entrega.trim()) {
      toast.error('Por favor ingresa tu dirección de entrega');
      return;
    }

    setLoading(true);
    try {
      // 1. Crear el pedido primero (incluye ciudad_id para conversión de moneda)
      const pedidoData = {
        restaurante_id: restauranteId,
        items: items.map(item => ({
          producto_id: item.producto_id,
          cantidad: item.cantidad,
          notas: item.notas
        })),
        direccion_entrega: formData.direccion_entrega,
        notas: formData.notas,
        metodo_pago: formData.metodo_pago,
        codigo_cupon: cuponAplicado?.codigo || null,
        diggii_usar: usarDiggii && diggiiUsar > 0 ? diggiiUsar : null,
        ciudad_id: ciudad.ciudad_id  // OBLIGATORIO
      };

      const pedido = await pedidosApi.create(pedidoData);
      
      // 2. Si es pago con tarjeta (Stripe), redirigir a Stripe Checkout
      if (formData.metodo_pago === 'tarjeta') {
        toast.loading('Redirigiendo al pago...', { id: 'payment' });
        
        const checkoutSession = await payments.createCheckoutSession(
          pedido.pedido_id,
          window.location.origin
        );
        
        // Redirigir a Stripe Checkout
        window.location.href = checkoutSession.url;
        return; // No limpiar carrito hasta que el pago sea exitoso
      }
      
      // 3. Si es MercadoPago, crear preferencia y redirigir
      if (formData.metodo_pago === 'mercadopago') {
        toast.loading('Redirigiendo a MercadoPago...', { id: 'payment' });
        
        try {
          const preference = await payments.createMercadoPagoPreference(
            pedido.pedido_id,
            `Pedido #${pedido.pedido_id.slice(-6)} - Mercapoli`,
            total,
            monedaCodigo
          );
          
          // Redirigir a MercadoPago Checkout (sandbox o producción según config)
          const checkoutUrl = preference.init_point || preference.sandbox_init_point;
          window.location.href = checkoutUrl;
          return;
        } catch (mpError) {
          toast.dismiss('payment');
          toast.error('MercadoPago no está disponible. Prueba otro método de pago.');
          setLoading(false);
          return;
        }
      }
      
      // 4. Si es efectivo, continuar como antes
      clearCart();
      
      if (diggiiUsar > 0) {
        toast.success(`¡Pedido realizado! Usaste ${diggiiUsar.toFixed(2)} DIGGII`);
      } else {
        toast.success('¡Pedido realizado con éxito!');
      }
      
      navigate(`/app/pedido/${pedido.pedido_id}`);
    } catch (error) {
      toast.dismiss('payment');
      toast.error(error.response?.data?.detail || 'Error al crear el pedido');
    }
    setLoading(false);
  };

  const paymentMethods = [
    { value: 'efectivo', label: 'Efectivo', icon: Banknote, description: 'Paga al recibir tu pedido' },
    { value: 'tarjeta', label: 'Tarjeta', icon: CreditCard, description: 'Débito o crédito (Stripe)' },
    { value: 'mercadopago', label: 'MercadoPago', icon: CreditCard, description: 'Paga con MercadoPago' },
  ];

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center h-16 gap-4">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-muted">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="font-heading text-lg font-bold">Finalizar pedido</h1>
          </div>
        </div>
      </header>

      <form onSubmit={handleSubmit}>
        <div className="container-app py-6 space-y-6">
          {/* Delivery address */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl border border-border p-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-spicy-coral" />
              <h2 className="font-heading font-bold">Dirección de entrega</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="direccion_entrega">Dirección completa *</Label>
                <Input
                  id="direccion_entrega"
                  name="direccion_entrega"
                  placeholder="Calle, número, colonia, referencias..."
                  value={formData.direccion_entrega}
                  onChange={handleChange}
                  className="mt-1"
                  required
                  data-testid="checkout-address"
                />
              </div>
              
              <div>
                <Label htmlFor="notas">Notas para el repartidor (opcional)</Label>
                <Textarea
                  id="notas"
                  name="notas"
                  placeholder="Ej: Tocar el timbre, dejar en la puerta..."
                  value={formData.notas}
                  onChange={handleChange}
                  className="mt-1 resize-none"
                  rows={2}
                />
              </div>
            </div>
          </motion.div>

          {/* DIGGII Section */}
          {diggiiData && diggiiData.balance > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 }}
              className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 rounded-xl border border-amber-500/30 p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Coins className="h-5 w-5 text-amber-500" />
                  <h2 className="font-heading font-bold">Usar DIGGII</h2>
                </div>
                <Switch
                  checked={usarDiggii}
                  onCheckedChange={handleDiggiiChange}
                  data-testid="use-diggii-switch"
                />
              </div>

              <div className="flex items-center justify-between text-sm mb-3">
                <span className="text-muted-foreground">Tu balance:</span>
                <span className="font-bold text-amber-600">
                  {diggiiData.balance.toFixed(2)} DIGGII = {diggiiData.simbolo}{diggiiData.valor_en_local.toFixed(2)}
                </span>
              </div>

              {usarDiggii && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4"
                >
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Cantidad a usar:</span>
                      <span className="font-bold text-amber-600">{diggiiUsar.toFixed(2)} DIGGII</span>
                    </div>
                    <Slider
                      value={[diggiiUsar]}
                      onValueChange={handleDiggiiSliderChange}
                      max={Math.min(diggiiData.balance, maxDiggiiPorPedido)}
                      step={0.1}
                      className="py-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0</span>
                      <span>{Math.min(diggiiData.balance, maxDiggiiPorPedido).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <div className="flex justify-between">
                      <span className="text-sm">Descuento aplicado:</span>
                      <span className="font-bold text-amber-600">
                        -{diggiiData.simbolo}{descuentoDiggii.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Cupón de descuento */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-card rounded-xl border border-border p-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <Tag className="h-5 w-5 text-zesty-lime" />
              <h2 className="font-heading font-bold">Cupón de descuento</h2>
            </div>

            {cuponAplicado ? (
              <div className="flex items-center justify-between p-3 bg-zesty-lime/10 rounded-lg border border-zesty-lime/30">
                <div>
                  <p className="font-medium text-zesty-lime">{cuponAplicado.codigo}</p>
                  <p className="text-sm text-muted-foreground">{cuponAplicado.descripcion}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-zesty-lime">-${descuento.toFixed(2)}</span>
                  <button
                    type="button"
                    onClick={removerCupon}
                    className="p-1 rounded-full hover:bg-muted"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  placeholder="Ingresa tu código"
                  value={codigoCupon}
                  onChange={(e) => setCodigoCupon(e.target.value.toUpperCase())}
                  className="flex-1"
                  data-testid="coupon-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={aplicarCupon}
                  disabled={cuponLoading}
                  data-testid="apply-coupon-btn"
                >
                  {cuponLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Aplicar'
                  )}
                </Button>
              </div>
            )}
          </motion.div>

          {/* Payment method */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-xl border border-border p-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="h-5 w-5 text-spicy-coral" />
              <h2 className="font-heading font-bold">Método de pago</h2>
            </div>

            <RadioGroup
              value={formData.metodo_pago}
              onValueChange={(value) => setFormData(prev => ({ ...prev, metodo_pago: value }))}
              className="space-y-3"
            >
              {paymentMethods.map((method) => (
                <label
                  key={method.value}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.metodo_pago === method.value
                      ? 'border-spicy-coral bg-spicy-coral/5'
                      : 'border-border hover:border-spicy-coral/50'
                  }`}
                >
                  <RadioGroupItem value={method.value} id={method.value} />
                  <method.icon className="h-6 w-6 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="font-medium">{method.label}</p>
                    <p className="text-sm text-muted-foreground">{method.description}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </motion.div>

          {/* Order summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-xl border border-border p-4"
          >
            <h2 className="font-heading font-bold mb-4">Resumen del pedido</h2>
            
            {restauranteInfo && (
              <p className="text-sm text-muted-foreground mb-3">
                De: {restauranteInfo.nombre}
              </p>
            )}

            <div className="space-y-2 mb-4">
              {items.map((item) => (
                <div key={item.producto_id} className="flex justify-between text-sm">
                  <span>
                    {item.cantidad}x {item.nombre}
                  </span>
                  <span>{monedaSimbolo}{(item.precio * item.cantidad).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <Separator className="my-3" />

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{monedaSimbolo}{subtotal.toFixed(2)}</span>
              </div>
              {descuento > 0 && (
                <div className="flex justify-between text-sm text-zesty-lime">
                  <span>Descuento ({cuponAplicado?.codigo})</span>
                  <span>-{monedaSimbolo}{descuento.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Envío</span>
                <span>{monedaSimbolo}{costoEnvio.toFixed(2)}</span>
              </div>
              {descuentoDiggii > 0 && (
                <div className="flex justify-between text-sm text-amber-600">
                  <span className="flex items-center gap-1">
                    <Coins className="h-3 w-3" />
                    DIGGII ({diggiiUsar.toFixed(2)})
                  </span>
                  <span>-{monedaSimbolo}{descuentoDiggii.toFixed(2)}</span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between font-heading font-bold text-lg">
                <span>Total</span>
                <span className="text-spicy-coral">{monedaSimbolo}{total.toFixed(2)}</span>
              </div>
              {/* Mostrar equivalente en USD */}
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Equivalente</span>
                <span>USD ${(total / tasaCambio).toFixed(2)} – tasa {tasaCambio.toFixed(2)}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Submit button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent">
          <div className="container-app">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-spicy-coral hover:bg-spicy-coral/90 py-6 rounded-full shadow-glow"
              data-testid="place-order-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-5 w-5" />
                  Confirmar pedido · {monedaSimbolo}{total.toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
